import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../config/apiConfig";
import {
  FaFacebook,
  FaTwitter,
  FaInstagram,
  FaLinkedin,
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
} from "react-icons/fa";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [popularRoutes, setPopularRoutes] = useState([]);
  const [popularCities, setPopularCities] = useState([]);
  const [footerRoutes, setFooterRoutes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {

        // Fetch each endpoint separately with individual error handling
        const promises = [
          axios
            .get(`${API_URL}/popular-routes`, { timeout: 5000 })
            .catch((err) => {
              console.warn("Popular routes unavailable:", err.message);
              return { data: [] };
            }),
          axios
            .get(`${API_URL}/popular-cities`, { timeout: 5000 })
            .catch((err) => {
              console.warn("Popular cities unavailable:", err.message);
              return { data: [] };
            }),
          axios
            .get(`${API_URL}/footer-routes`, { timeout: 5000 })
            .catch((err) => {
              console.warn("Footer routes unavailable:", err.message);
              return { data: [] };
            }),
        ];

        const [routesRes, citiesRes, footerRoutesRes] =
          await Promise.all(promises);

        setPopularRoutes(routesRes.data || []);
        setPopularCities(citiesRes.data || []);
        setFooterRoutes(footerRoutesRes.data || []);
      } catch (error) {
        console.error("Error fetching footer data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <footer className="bg-secondary text-white pt-8 sm:pt-10 md:pt-12 lg:pt-16 pb-4 sm:pb-6 md:pb-8">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
          {/* Company Info */}
          <div className="mb-6 sm:mb-0">
            <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4">
              UrbanRide
            </h3>
            <p className="text-gray-400 text-sm sm:text-base mb-3 sm:mb-4">
              Book outstation cabs at the lowest prices with clean cars,
              experienced drivers and transparent billing.
            </p>
            <div className="flex space-x-4">
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-primary transition"
              >
                <FaFacebook />
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-primary transition"
              >
                <FaTwitter />
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-primary transition"
              >
                <FaInstagram />
              </a>
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-primary transition"
              >
                <FaLinkedin />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="mb-6 sm:mb-0">
            <h3 className="text-lg sm:text-xl font-bold mb-4">Quick Links</h3>
            <ul className="text-gray-400 text-sm sm:text-base space-y-3">
              <li>
                <Link
                  to="/about"
                  className="hover:text-primary transition-colors duration-200"
                >
                  About Us
                </Link>
              </li>
              <li>
                <Link
                  to="/services"
                  className="hover:text-primary transition-colors duration-200"
                >
                  Services
                </Link>
              </li>
              <li>
                <Link
                  to="/blog"
                  className="hover:text-primary transition-colors duration-200"
                >
                  Blog
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="hover:text-primary transition-colors duration-200"
                >
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="mb-6 sm:mb-0">
            <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4">
              Contact Us
            </h3>
            <ul className="text-gray-400 text-sm sm:text-base space-y-3">
              <li className="flex items-center">
                <FaMapMarkerAlt className="mr-2 text-primary" /> Rajkot,Gujarat
              </li>
              <li className="flex items-center">
                <FaPhoneAlt className="mr-2 text-primary" /> +91 7096386636
              </li>
              <li className="flex items-center">
                <FaEnvelope className="mr-2 text-primary" />{" "}
                info@rajeshwarinfotech.com
              </li>
            </ul>
          </div>
        </div>

        {/* Popular Cities & Fixed Routes */}
        {!loading && (popularCities.length > 0 || popularRoutes.length > 0) && (
          <div className="mt-8 pt-6 border-t border-gray-700">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {popularCities.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold mb-4 text-white">Popular Cities</h3>
                  <div className="text-gray-400 text-sm leading-relaxed">
                    {popularCities.map((city, index) => (
                      <span key={city._id}>
                        <span className="text-gray-400">{city.name}</span>
                        {index < popularCities.length - 1 && " | "}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {popularRoutes.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold mb-4 text-white">Fixed Routes</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    <ul className="text-gray-400 text-sm space-y-2">
                      {popularRoutes.map((route) => (
                        <li key={route._id}>
                          <Link
                            to={`/cabs?from=${encodeURIComponent(route.from)}&to=${encodeURIComponent(route.to)}&journeyType=outstation&tripType=oneWay`}
                            className="hover:text-primary transition-colors duration-200"
                          >
                            {route.from} → {route.to}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Premium Routes Section */}
        {!loading && footerRoutes.length > 0 && (
          <div className="mt-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {footerRoutes.map((category) => (
                <div key={category._id}>
                  <h4 className="text-md font-semibold mb-3 text-white">
                    {category.category}
                  </h4>
                  <ul className="text-gray-400 text-sm space-y-2">
                    {category.routes.slice(0, 6).map((route, index) => (
                      <li key={index}>
                        <Link
                          to={`/route/${route.from.toLowerCase().replace(/\s+/g, "-")}-to-${route.to.toLowerCase().replace(/\s+/g, "-")}`}
                          className="hover:text-primary transition-colors duration-200"
                        >
                          {route.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer Bottom */}
        <hr className="border-gray-700 my-6 sm:my-8" />
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 text-xs sm:text-sm mb-3 md:mb-0 text-center md:text-left">
            &copy; {currentYear} UrbanRide. All rights reserved.
          </p>
          <div className="flex flex-wrap justify-center gap-4 md:space-x-6">
            <Link
              to="/terms"
              className="text-gray-400 hover:text-primary transition-colors duration-200 text-xs sm:text-sm px-2"
            >
              Terms & Conditions
            </Link>
            <Link
              to="/privacy"
              className="text-gray-400 hover:text-primary transition-colors duration-200 text-xs sm:text-sm px-2"
            >
              Privacy Policy
            </Link>
            <Link
              to="/refund"
              className="text-gray-400 hover:text-primary transition-colors duration-200 text-xs sm:text-sm px-2"
            >
              Refund Policy
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
